import { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from '@shared/components';
import type { Customer, Employee } from '@shared/types';
import type { LimitType, PaymentDate } from '@modules/master';
import type { CustomerCreateInput } from '../services/customer.service';
import { checkCustomerDuplicate, fetchCustomers } from '../services/customer.service';
import { fetchEmployees } from '@modules/master';
import { fetchLimitTypes } from '../../master/services/limitType.service';
import { fetchPaymentDates } from '../../master/services/paymentDate.service';
import { CustomerDuplicateWarning } from './CustomerDuplicateWarning';
import { CustomerFormFieldsBasic } from './CustomerFormFieldsBasic';
import { CustomerFormFieldsAddress } from './CustomerFormFieldsAddress';
import { CustomerFormFieldsTrade } from './CustomerFormFieldsTrade';

const DEFAULT: CustomerCreateInput = {
  name: '', nameKana: '', rank: 'C', rankMode: 'auto', status: 'prospect', partnerTypes: ['customer'], industry: '',
  postalCode: '', prefecture: '',
  phone: '', fax: null, email: null, website: null,
  assignedUserId: null, paymentTerms: 30, notes: null,
  parentId: null, areaId: null, limitTypeId: '', paymentDateId: '',
  taxCalcMode: 'floor', tel2: null, group: null,
  addr1: '', addr2: '', addr3: '', department: '',
};

function toInput(c: Customer): CustomerCreateInput {
  return {
    name: c.name, nameKana: c.nameKana, rank: c.rank, rankMode: c.rankMode ?? 'auto', status: c.status,
    partnerTypes: c.partnerTypes && c.partnerTypes.length > 0 ? c.partnerTypes : ['customer'],
    industry: c.industry, postalCode: c.postalCode, prefecture: c.prefecture,
    phone: c.phone,
    fax: c.fax, email: c.email, website: c.website,
    assignedUserId: c.assignedUserId, paymentTerms: c.paymentTerms, notes: c.notes,
    parentId: c.parentId ?? null, areaId: c.areaId ?? null,
    limitTypeId: c.limitTypeId ?? '', paymentDateId: c.paymentDateId ?? '',
    taxCalcMode: c.taxCalcMode ?? 'floor', tel2: c.tel2 ?? null,
    group: c.group ?? null, addr1: c.addr1 ?? '', addr2: c.addr2 ?? '', addr3: c.addr3 ?? '',
    department: c.department ?? '',
  };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  customer?: Customer | null;
  onSubmit: (data: CustomerCreateInput) => Promise<void>;
}

export function CustomerFormModal({ isOpen, onClose, customer, onSubmit }: Props) {
  const [form, setForm] = useState<CustomerCreateInput>(customer ? toInput(customer) : DEFAULT);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [limitTypes, setLimitTypes] = useState<LimitType[]>([]);
  const [paymentDates, setPaymentDates] = useState<PaymentDate[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [dupCount, setDupCount] = useState(0);
  const upd = useCallback((patch: Partial<CustomerCreateInput>) => setForm((f) => ({ ...f, ...patch })), []);

  useEffect(() => {
    void fetchEmployees().then(setEmployees).catch(() => setEmployees([]));
    void fetchLimitTypes().then(setLimitTypes).catch(() => setLimitTypes([]));
    void fetchPaymentDates().then(setPaymentDates).catch(() => setPaymentDates([]));
    void fetchCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);

  const handleBlurDuplicate = useCallback(async () => {
    if (!form.name && !form.nameKana) return;
    try {
      const result = await checkCustomerDuplicate(
        { name: form.name || undefined, nameKana: form.nameKana || undefined },
        customer?.id
      );
      const unique = new Set(result.matches.map((m) => m.customerId));
      setDupCount(unique.size);
    } catch {
      setDupCount(0);
    }
  }, [form.name, form.nameKana, customer?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    try { await onSubmit(form); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={customer ? '顧客編集' : '顧客登録'} size="lg" variant="drawer">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <CustomerDuplicateWarning count={dupCount} />
        <CustomerFormFieldsBasic form={form} upd={upd} onBlurDuplicate={() => void handleBlurDuplicate()} />
        <CustomerFormFieldsAddress form={form} upd={upd} />
        <CustomerFormFieldsTrade
          form={form}
          upd={upd}
          employees={employees}
          limitTypes={limitTypes}
          paymentDates={paymentDates}
          customers={customers}
          editingCustomerId={customer?.id ?? null}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
